/**
 * avatarViewApply — flags every material under a root for the requested mesh
 * view mode. Pure over the material objects it is handed (no THREE import), so
 * it is unit-tested with plain objects and the Canvas component that calls it
 * stays a few lines.
 *
 * Modes (avatarViewStore.MESH_VIEWS):
 *   lit        the materials as authored.
 *   unlit      base colour only, and it is a TOGGLE ON the material rather than
 *              a material swap, so the textures, the graph and its live params
 *              stay exactly where they are: `material.fragmentNode` is set to
 *              the node built alongside the graph and parked on
 *              `userData.unlitNode` (avatarMaterial), since by toggle time the
 *              graph is long gone. That makes the material output the node
 *              directly and skip the lighting model.
 *
 *              A MATERIAL CARRYING NO SUCH NODE IS LEFT ALONE, and the node's
 *              absence IS the answer — there is no second mechanism to fall
 *              back to. There used to be: a GLSL `AOS_UNLIT` define, which the
 *              graph compiler spliced in before <opaque_fragment>. Nothing
 *              splices GLSL any more (`onBeforeCompile` is never called under
 *              `WebGPURenderer`), so setting it was one recompile for a branch
 *              no shader carried.
 *   wireframe  three's own `material.wireframe`.
 *
 * Each call is idempotent and touches `needsUpdate` only where something
 * actually changed, because that flag recompiles the program.
 */

function eachMaterial(root, fn) {
  root.traverse((o) => {
    if (!o.isMesh || !o.material) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const m of mats) fn(m, o)
  })
}

/**
 * Returns how many materials changed. `mode` must be one of MESH_VIEWS; the
 * caller validates (the store already did).
 */
export function applyMeshView(root, mode) {
  if (!root) return 0
  const wire = mode === 'wireframe'
  const unlit = mode === 'unlit'
  let changed = 0
  eachMaterial(root, (m) => {
    let touched = false
    if (!!m.wireframe !== wire) {
      m.wireframe = wire
      touched = true
    }
    // A node material is recognised by CARRYING the node, not by a type check or
    // a flag: this module is pure over plain objects on purpose, and "has an
    // unlit node" is the same question as "can this be shown unlit".
    const unlitNode = m.userData?.unlitNode
    if (unlitNode) {
      const isUnlit = m.fragmentNode === unlitNode
      if (unlit !== isUnlit) {
        m.fragmentNode = unlit ? unlitNode : null
        m.needsUpdate = true
        touched = true
      }
    }
    if (touched) changed++
  })
  return changed
}
